/*
 * PRISM cart — AJAX cart engine + drawer.
 *
 * Provides a small promise-based API over the Shopify Cart AJAX API and a
 * <prism-cart-drawer> custom element that refreshes itself from server-rendered
 * HTML (Section Rendering API) so money/locale formatting stays authoritative.
 *
 * Public API (window.PRISM.cart):
 *   add(items, options)   POST /cart/add.js   items: object | array of line items
 *   change(payload)       POST /cart/change.js {id|line, quantity, properties}
 *   getState()            GET  /cart.js        -> full cart JSON
 *   openDrawer() / closeDrawer()
 *
 * Drawer markup contract (rendered by the cart-drawer section):
 *   <prism-cart-drawer id="PrismCartDrawer" data-cart-section="<section-id>">
 *     <div data-cart-overlay></div>
 *     <div class="prism-cart-drawer__panel" role="dialog" aria-modal="true">
 *       <button data-cart-close></button>
 *       <div data-cart-contents> ...line items, totals... </div>
 *     </div>
 *   </prism-cart-drawer>
 *
 * Triggers anywhere in the theme:
 *   [data-cart-toggle]                 opens the drawer
 *   [data-cart-count]                  text set to cart.item_count
 *   input[data-cart-quantity][data-line] change -> updates that line
 *   [data-cart-remove][data-line]      click  -> removes that line
 */
window.PRISM = window.PRISM || {};

(function (PRISM) {
  'use strict';

  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function request(path, body) {
    var opts = {
      method: body ? 'POST' : 'GET',
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
    };
    if (body) {
      opts.headers['Content-Type'] = 'application/json';
      opts.body = JSON.stringify(body);
    }
    return fetch(root() + path, opts).then(function (response) {
      return response.json().then(function (data) {
        if (!response.ok) {
          var err = new Error((data && data.description) || (data && data.message) || 'Cart request failed');
          err.data = data;
          throw err;
        }
        return data;
      });
    });
  }

  var cart = {
    getState: function () {
      return request('cart.js');
    },

    add: function (items, options) {
      options = options || {};
      var body = items.items ? items : { items: [].concat(items) };
      if (options.sections) body.sections = options.sections;
      return request('cart/add.js', body)
        .then(function (added) {
          return cart.getState().then(function (state) {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: options.source || 'add',
              cartData: state,
              sections: added.sections,
              added: added,
            });
            return state;
          });
        })
        .catch(function (err) {
          publish(PUB_SUB_EVENTS.cartError, {
            source: options.source || 'add',
            message: err.message,
            errors: err.data,
          });
          throw err;
        });
    },

    change: function (payload) {
      var body = { quantity: payload.quantity };
      if (payload.id != null) body.id = payload.id;
      if (payload.line != null) body.line = payload.line;
      if (payload.properties) body.properties = payload.properties;
      if (payload.sections) body.sections = payload.sections;
      return request('cart/change.js', body).then(function (state) {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: payload.source || 'change',
          cartData: state,
          sections: state.sections,
        });
        return state;
      });
    },

    openDrawer: function () {
      var drawer = document.querySelector('prism-cart-drawer');
      if (drawer && typeof drawer.open === 'function') drawer.open();
    },

    closeDrawer: function () {
      var drawer = document.querySelector('prism-cart-drawer');
      if (drawer && typeof drawer.close === 'function') drawer.close();
    },
  };

  PRISM.cart = cart;

  /* ----- Cart drawer custom element ----- */
  if (!customElements.get('prism-cart-drawer')) {
    customElements.define(
      'prism-cart-drawer',
      class PrismCartDrawer extends HTMLElement {
        connectedCallback() {
          this.overlay = this.querySelector('[data-cart-overlay]');
          this.panel = this.querySelector('.prism-cart-drawer__panel');
          this.contents = this.querySelector('[data-cart-contents]');
          this.sectionId = this.getAttribute('data-cart-section');

          this.onKeydown = this.onKeydown.bind(this);
          this.close = this.close.bind(this);

          if (this.overlay) this.overlay.addEventListener('click', this.close);
          this.addEventListener('click', (e) => {
            if (e.target.closest('[data-cart-close]')) this.close();
          });

          this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, (payload) => {
            this.render(payload);
            if (payload && payload.source === 'add') this.open();
          });
        }

        disconnectedCallback() {
          if (this.unsubscribe) this.unsubscribe();
          document.removeEventListener('keydown', this.onKeydown);
        }

        onKeydown(e) {
          if (e.key === 'Escape') this.close();
        }

        open() {
          if (this.hasAttribute('open')) return;
          this.setAttribute('open', '');
          document.documentElement.classList.add('prism-scroll-lock');
          document.addEventListener('keydown', this.onKeydown);
          if (PRISM.trapFocus && this.panel) this.releaseFocus = PRISM.trapFocus(this.panel);
        }

        close() {
          if (!this.hasAttribute('open')) return;
          this.removeAttribute('open');
          document.documentElement.classList.remove('prism-scroll-lock');
          document.removeEventListener('keydown', this.onKeydown);
          if (this.releaseFocus) {
            this.releaseFocus();
            this.releaseFocus = null;
          }
        }

        // Swap in freshly rendered HTML from the Section Rendering API payload.
        render(payload) {
          if (this.contents && payload && payload.sections && this.sectionId && payload.sections[this.sectionId]) {
            var doc = new DOMParser().parseFromString(payload.sections[this.sectionId], 'text/html');
            var fresh = doc.querySelector('[data-cart-contents]');
            if (fresh) this.contents.innerHTML = fresh.innerHTML;
          }
        }
      }
    );
  }

  /* ----- Theme-wide glue: triggers, count, line edits ----- */
  document.addEventListener('click', function (e) {
    var toggle = e.target.closest('[data-cart-toggle]');
    if (toggle && document.querySelector('prism-cart-drawer')) {
      e.preventDefault();
      cart.openDrawer();
      return;
    }
    var remove = e.target.closest('[data-cart-remove][data-line]');
    if (remove) {
      e.preventDefault();
      cart.change({ line: parseInt(remove.getAttribute('data-line'), 10), quantity: 0, sections: drawerSections() });
    }
  });

  document.addEventListener('change', function (e) {
    var input = e.target.closest('input[data-cart-quantity][data-line]');
    if (!input) return;
    var qty = Math.max(0, parseInt(input.value, 10) || 0);
    cart.change({ line: parseInt(input.getAttribute('data-line'), 10), quantity: qty, sections: drawerSections() });
  });

  function drawerSections() {
    var drawer = document.querySelector('prism-cart-drawer[data-cart-section]');
    return drawer ? [drawer.getAttribute('data-cart-section')] : null;
  }

  subscribe(PUB_SUB_EVENTS.cartUpdate, function (payload) {
    if (!payload || !payload.cartData) return;
    var count = payload.cartData.item_count;
    document.querySelectorAll('[data-cart-count]').forEach(function (el) {
      el.textContent = count;
      el.toggleAttribute('hidden', count === 0);
    });
  });
})(window.PRISM);
